import { useInvalidateActivityLogs } from '@/features/goal/services/activityQueries';
import { activityService } from '@/features/goal/services/activityService';
import { goalService } from '@/features/goal/services/goalService';
import {
    useCommentMentionNotification,
    useCommentNotification,
    useReplyNotification,
} from '@/features/notification/services/factoryNotificationServices';
import {
    checkSendNotificationByStatusCode,
    checkSendNotificationByTodoStatus,
} from '@/features/notification/utils/notificationUtils';
import { todoService } from '@/features/todo/services/todoService';
import { useAuthStore } from '@/stores/authStore';
import {
    QueryClient,
    useMutation,
    useQuery,
    useQueryClient,
} from '@tanstack/react-query';
import { CommentWithMentionsAndUser } from '../../../common/types/databaseTypes';
import { CreateCommentParams, commentService } from './commentService';
import type {
    Notification,
    Comment,
    CommentMention,
    NoteWithDetails,
    UserWithDisplaySettings,
} from '@/common/types/databaseTypes';
import i18next from '@/translation/i18n';
import { workspaceService } from '@/features/workspace/services/workspaceService';
import { removeMentionMarkup } from '../utils/mentionUtils';
import { useDeleteNotificationWithRelatedData } from '@/features/notification/services/useNotificationQueries';
import {
    CommentContent,
    CommentMentionContent,
    NotificationType,
    ReplyContent,
} from '@/features/notification/constants/notification';
import { useSafeQuery } from '@/common/hooks/useSafeQuery';
import { supabase } from '@tickitacka/shared';

export type CommentTargetType =
    | 'goal'
    | 'todo'
    | 'note'
    | 'feedback'
    | 'daily_scrum';

// 목표와 할일 모두에서 사용할 수 있는 통합 댓글 쿼리 훅
export function useComments(
    type: 'goal' | 'todo' | 'note',
    targetId: string | undefined
) {
    return useSafeQuery<CommentWithMentionsAndUser[]>({
        queryKey: [
            type === 'goal'
                ? 'goalComments'
                : type === 'todo'
                  ? 'todoComments'
                  : 'noteComments',
            targetId,
        ],
        queryFn: () => {
            if (!targetId) return Promise.resolve([]);

            if (type === 'goal') {
                return commentService.getComments('goal_id', targetId);
            } else if (type === 'todo') {
                return commentService.getComments('todo_id', targetId);
            } else {
                return commentService.getComments('note_id', targetId);
            }
        },
        enabled: !!targetId,
        userFields: ['user', 'children'],
    });
}

// 댓글 개수만 조회하는 훅 (공통)
export function useCommentsCount(
    type: 'goal' | 'todo' | 'note',
    targetId: string | undefined
) {
    return useQuery<number>({
        queryKey: [
            type === 'goal'
                ? 'goalComments'
                : type === 'todo'
                  ? 'todoComments'
                  : 'noteComments',
            targetId,
            'count',
        ],
        queryFn: () => {
            if (!targetId) return Promise.resolve(0);

            if (type === 'goal') {
                return commentService.getCommentsCount('goal_id', targetId);
            } else if (type === 'todo') {
                return commentService.getCommentsCount('todo_id', targetId);
            } else {
                return commentService.getCommentsCount('note_id', targetId);
            }
        },
        enabled: !!targetId,
    });
}

// 공통 알림 관리 타입 정의
interface NotificationContext {
    user: UserWithDisplaySettings;
    createdComment: Comment;
    commentContent: string;
    mentions: CommentMention[];
    createCommentNotification: (params: {
        userId: string;
        commentId: string;
        commentContent: CommentContent;
    }) => Promise<Notification>;
    createCommentMentionNotification: (params: {
        userId: string;
        commentMentionContent: CommentMentionContent;
    }) => Promise<Notification>;
    createReplyNotification: (params: {
        userId: string;
        replyId: string;
        replyContent: ReplyContent;
    }) => Promise<Notification>;
}

interface ContentInfo {
    id: string;
    title: string;
    ownerId?: string;
    goalId?: string;
    leaderId?: string;
}

// 멘션 알림 전송 공통 함수
function sendMentionNotifications(
    context: NotificationContext,
    contentInfo: ContentInfo,
    parentType: string
) {
    const {
        user,
        createdComment,
        commentContent,
        mentions,
        createCommentMentionNotification,
    } = context;

    if (mentions.length === 0) return [];

    // 중복 사용자 ID 제거
    const uniqueMentionedUserIds = [
        ...new Set(
            mentions.filter(m => m.user_id !== user.id).map(m => m.user_id)
        ),
    ];

    // 각 멘션 사용자에게 알림 발송 작업 추가
    return uniqueMentionedUserIds.map(userId =>
        createCommentMentionNotification({
            userId,
            commentMentionContent: {
                mentioner_name: user.name,
                mentioner_image_url: user.profile_image_url || '',
                mentioner_id: user.id,
                comment_id: createdComment.id,
                comment_text: commentContent,
                parent_type: parentType,
                parent_title: contentInfo.title,
                parent_id: contentInfo.id,
            },
        })
    );
}

// 답글 알림 처리 공통 함수
async function handleReplyNotification(
    context: NotificationContext,
    contentInfo: ContentInfo,
    parentType: string
) {
    const { user, createdComment, commentContent, createReplyNotification } =
        context;

    if (!createdComment.parent_id) return null;

    const parentComment = await commentService.getCommentById(
        createdComment.parent_id
    );
    if (!parentComment || parentComment.register_id === user.id) return null;

    // 워크스페이스 멤버십 체크
    const goalId =
        contentInfo.goalId ||
        (parentComment.goal_id ? parentComment.goal_id : null);
    if (goalId) {
        const isMember =
            await workspaceService.checkWorkspaceMembershipByGoalId(
                goalId,
                parentComment.register_id
            );
        if (!isMember) return null;
    }

    return createReplyNotification({
        userId: parentComment.register_id,
        replyId: createdComment.id,
        replyContent: {
            replier_name: user.name,
            replier_image_url: user.profile_image_url || '',
            replier_id: user.id,
            reply_text: commentContent,
            comment_id: parentComment.id,
            parent_type: parentType,
            parent_title: contentInfo.title,
            parent_id: contentInfo.id,
        },
    });
}

// 댓글 알림 처리 공통 함수
async function handleCommentNotification(
    context: NotificationContext,
    contentInfo: ContentInfo,
    parentType: string,
    targetUserIds: string[]
) {
    const { user, createdComment, commentContent, createCommentNotification } =
        context;

    const filteredUserIds = targetUserIds.filter(userId => userId !== user.id);
    if (filteredUserIds.length === 0) return [];

    const notificationPromises = await Promise.all(
        filteredUserIds.map(async userId => {
            // 워크스페이스 멤버십 체크
            if (contentInfo.goalId) {
                const isMember =
                    await workspaceService.checkWorkspaceMembershipByGoalId(
                        contentInfo.goalId,
                        userId
                    );
                if (!isMember) return null;
            }

            return createCommentNotification({
                userId,
                commentId: createdComment.id,
                commentContent: {
                    commenter_name: user.name,
                    commenter_image_url: user.profile_image_url || '',
                    commenter_id: user.id,
                    comment_text: commentContent,
                    parent_type: parentType,
                    parent_title: contentInfo.title,
                    parent_id: contentInfo.id,
                },
            });
        })
    );

    return notificationPromises.filter(promise => promise !== null);
}

// 노트 알림 처리
async function handleNoteNotifications(
    context: NotificationContext,
    variables: CreateCommentParams,
    queryClient: QueryClient
) {
    try {
        if (!variables.note_id || typeof variables.note_id !== 'string') {
            console.warn('노트 ID가 유효하지 않아 알림을 보낼 수 없습니다.');
            return;
        }

        const noteId = variables.note_id;
        const noteData = queryClient.getQueryData<NoteWithDetails>([
            'note',
            noteId,
        ]);
        const contentInfo: ContentInfo = {
            id: noteId,
            title: noteData?.title || i18next.t('note:title'),
            ownerId: noteData?.register_id,
            goalId: noteData?.goal_id,
        };

        const notificationPromises = [];

        // 멘션 알림
        const mentionPromises = sendMentionNotifications(
            context,
            contentInfo,
            'note'
        );
        notificationPromises.push(...mentionPromises);

        // 댓글/답글 분기 처리
        if (context.createdComment.parent_id) {
            // 답글 알림
            const replyPromise = await handleReplyNotification(
                context,
                contentInfo,
                'note'
            );
            if (replyPromise) notificationPromises.push(replyPromise);
        } else {
            // 댓글 알림 (노트 작성자에게)
            if (
                contentInfo.ownerId &&
                contentInfo.ownerId !== context.user.id
            ) {
                const commentPromises = await handleCommentNotification(
                    context,
                    contentInfo,
                    'note',
                    [contentInfo.ownerId]
                );
                notificationPromises.push(...commentPromises);
            }
        }

        await Promise.all(notificationPromises);
    } catch (error: unknown) {
        const errorMessage =
            error instanceof Error ? error.message : '알 수 없는 오류';
        console.error('노트 댓글 알림 생성 오류:', errorMessage);
    }
}

// 목표 알림 처리
async function handleGoalNotifications(
    context: NotificationContext,
    variables: CreateCommentParams
) {
    const goal = await goalService.getGoalById(variables.goal_id);
    if (!goal) return;

    // 활성화 상태 체크
    if (!checkSendNotificationByStatusCode(goal, { isCommentChange: true })) {
        return;
    }

    const contentInfo: ContentInfo = {
        id: goal.id,
        title: goal.title,
        leaderId: goal.leader_id,
        goalId: goal.id,
    };

    const notificationPromises = [];

    // 멘션 알림
    const mentionPromises = sendMentionNotifications(
        context,
        contentInfo,
        'goal'
    );
    notificationPromises.push(...mentionPromises);

    // 댓글/답글 분기 처리
    if (context.createdComment.parent_id) {
        // 답글 알림
        const replyPromise = await handleReplyNotification(
            context,
            contentInfo,
            'goal'
        );
        if (replyPromise) notificationPromises.push(replyPromise);
    } else {
        // 댓글 알림 (목표 참여자들에게)
        const participantIds = await goalService.getGoalParticipantIdsByGoalId(
            variables.goal_id
        );
        const uniqueParticipantIds = [
            ...new Set([...participantIds, goal.leader_id]),
        ];

        const commentPromises = await handleCommentNotification(
            context,
            contentInfo,
            'goal',
            uniqueParticipantIds
        );
        notificationPromises.push(...commentPromises);
    }

    await Promise.all(notificationPromises);
}

// 할일 알림 처리
async function handleTodoNotifications(
    context: NotificationContext,
    variables: CreateCommentParams
) {
    const todo = await todoService.getTodoById(variables.todo_id);
    if (!todo) return;

    // 활성화 상태 체크
    if (!checkSendNotificationByTodoStatus(todo, { isCommentChange: true })) {
        return;
    }

    const contentInfo: ContentInfo = {
        id: todo.id,
        title: todo.title,
        ownerId: todo.user_id,
        goalId: todo.goal_id,
    };

    const notificationPromises = [];

    // 멘션 알림
    const mentionPromises = sendMentionNotifications(
        context,
        contentInfo,
        'todo'
    );
    notificationPromises.push(...mentionPromises);

    // 댓글/답글 분기 처리
    if (context.createdComment.parent_id) {
        // 답글 알림
        const replyPromise = await handleReplyNotification(
            context,
            contentInfo,
            'todo'
        );
        if (replyPromise) notificationPromises.push(replyPromise);
    } else {
        // 댓글 알림 (할일 담당자에게)
        if (todo.user_id !== context.user.id) {
            const commentPromises = await handleCommentNotification(
                context,
                contentInfo,
                'todo',
                [todo.user_id]
            );
            notificationPromises.push(...commentPromises);
        }
    }

    await Promise.all(notificationPromises);
}

// 댓글 추가 훅 (공통)
export function useAddComment(type: 'goal' | 'todo' | 'note') {
    const queryClient = useQueryClient();

    // 알림 발송 함수 참조
    const { createCommentNotification } = useCommentNotification();
    const { createCommentMentionNotification } =
        useCommentMentionNotification();
    const { createReplyNotification } = useReplyNotification();
    const invalidateActivityLogs = useInvalidateActivityLogs();

    return useMutation({
        mutationFn: (params: CreateCommentParams) =>
            commentService.addComment(params),
        onSuccess: async ({ comment: createdComment, mentions }, variables) => {
            // 쿼리 무효화
            await queryClient.invalidateQueries({
                queryKey: [
                    type === 'goal'
                        ? 'goalComments'
                        : type === 'todo'
                          ? 'todoComments'
                          : 'noteComments',
                    variables[
                        type === 'goal'
                            ? 'goal_id'
                            : type === 'todo'
                              ? 'todo_id'
                              : 'note_id'
                    ],
                ],
            });
            await queryClient.invalidateQueries({ queryKey: ['planners'] });
            await queryClient.invalidateQueries({
                queryKey: ['todos', 'inbox'],
            });

            if (type === 'goal') {
                invalidateActivityLogs(variables.goal_id);
            }

            // 알림 전송
            try {
                const { user } = useAuthStore.getState();
                if (!user) return;

                const commentContent = removeMentionMarkup(
                    createdComment.content
                );

                const notificationContext: NotificationContext = {
                    user,
                    createdComment,
                    commentContent,
                    mentions,
                    createCommentNotification,
                    createCommentMentionNotification,
                    createReplyNotification,
                };

                // 타입별 알림 처리
                switch (type) {
                    case 'note':
                        await handleNoteNotifications(
                            notificationContext,
                            variables,
                            queryClient
                        );
                        break;
                    case 'goal':
                        await handleGoalNotifications(
                            notificationContext,
                            variables
                        );
                        break;
                    case 'todo':
                        await handleTodoNotifications(
                            notificationContext,
                            variables
                        );
                        break;
                }
            } catch (error) {
                console.error('알림 발송 실패:', error);
                // 주요 업데이트는 이미 성공했으므로 에러를 무시
            }
        },
    });
}

// 댓글 수정 훅 (공통)
export function useUpdateComment(
    type: 'goal' | 'todo' | 'note',
    parentId: string | undefined
) {
    const queryClient = useQueryClient();
    const invalidateActivityLogs = useInvalidateActivityLogs();

    const { user } = useAuthStore.getState();
    const { createCommentMentionNotification } =
        useCommentMentionNotification();
    const deleteNotification = useDeleteNotificationWithRelatedData();

    return useMutation({
        mutationFn: ({
            commentId,
            content,
            mentions,
        }: {
            commentId: string;
            content: string;
            mentions: CommentMention[];
        }) => commentService.updateComment(commentId, content, mentions),
        onSuccess: async ({
            comment,
            addedMentionUids,
            removedMentionUids,
        }) => {
            if (parentId) {
                const queryKey = [
                    type === 'goal'
                        ? 'goalComments'
                        : type === 'todo'
                          ? 'todoComments'
                          : 'noteComments',
                    parentId,
                ];
                await queryClient.invalidateQueries({ queryKey });
                await queryClient.invalidateQueries({ queryKey: ['planners'] });
                await queryClient.invalidateQueries({
                    queryKey: ['todos', 'inbox'],
                });

                if (type === 'goal') {
                    invalidateActivityLogs(parentId);
                }
            }

            // 멘션 알림 추가 및 삭제
            if (comment?.id) {
                if (user) {
                    // parent title 조회
                    const parentTitle = await supabase
                        .from(type)
                        .select('title')
                        .eq('id', parentId)
                        .single()
                        .then(res => res.data?.title || '');

                    const notificationPromises = [];
                    // 새로 멘션된 사용자에게 알림 발송
                    addedMentionUids
                        .filter(uid => uid !== user?.id)
                        .map(uid => {
                            return notificationPromises.push(
                                createCommentMentionNotification({
                                    userId: uid,
                                    commentMentionContent: {
                                        mentioner_name: user.name,
                                        mentioner_image_url:
                                            user.profile_image_url || '',
                                        mentioner_id: user.id,
                                        comment_id: comment.id,
                                        comment_text: removeMentionMarkup(
                                            comment.content
                                        ),
                                        parent_type: type,
                                        parent_title: parentTitle,
                                        parent_id: parentId,
                                    },
                                })
                            );
                        });

                    // 멘션에서 제거된 사용자의 알림 삭제
                    removedMentionUids
                        .filter(uid => uid !== user?.id)
                        .map(uid => {
                            return notificationPromises.push(
                                deleteNotification.mutateAsync({
                                    userId: uid,
                                    relatedId: comment.id,
                                    type: NotificationType.COMMENT_MENTION,
                                })
                            );
                        });

                    await Promise.all(notificationPromises);
                }
            }
        },
    });
}

// 댓글 삭제 훅 (공통)
export function useDeleteComment(
    type: 'goal' | 'todo' | 'note',
    parentId: string | undefined
) {
    const queryClient = useQueryClient();
    const deleteCommentActivity = useDeleteCommentActivity();
    const invalidateActivityLogs = useInvalidateActivityLogs();

    return useMutation({
        mutationFn: (commentId: string) =>
            commentService.deleteComment(commentId),
        onSuccess: async (_, commentId) => {
            await deleteCommentActivity.mutateAsync(commentId);

            if (parentId) {
                const queryKey = [
                    type === 'goal'
                        ? 'goalComments'
                        : type === 'todo'
                          ? 'todoComments'
                          : 'noteComments',
                    parentId,
                ];
                await queryClient.invalidateQueries({ queryKey });
                await queryClient.invalidateQueries({ queryKey: ['planners'] });
                await queryClient.invalidateQueries({
                    queryKey: ['todos', 'inbox'],
                });

                if (type === 'goal') {
                    invalidateActivityLogs(parentId);
                }
            }
        },
    });
}

// 댓글 액티비티 삭제
function useDeleteCommentActivity() {
    return useMutation({
        mutationFn: (commentId: string) =>
            activityService.deleteActivityLog({
                activityType: 'comment',
                referenceId: commentId,
            }),
    });
}

// 멘션 가능한 유저(=워크스페이스 멤버) 조회 훅
export function useMentionableUsers(
    contentType: 'goal' | 'todo' | 'note' | 'feedback' | 'daily_scrum',
    contentId: string | undefined
) {
    return useQuery({
        queryKey: ['mentionable-users', contentType, contentId],
        queryFn: () =>
            commentService.getMentionableUsers(contentType, contentId),
        enabled: !!contentId,
    });
}
